import { useEffect, useRef, useState } from "react";
import { Link, Prompt } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  message,
  Modal,
  Popconfirm,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  SaveOutlined,
  SendOutlined,
  CheckOutlined,
  RollbackOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { useAppRole, useCanEdit } from "@/hooks/usePermission";
import {
  approveReportDraft,
  downloadReportFinalExcel,
  getReportDraft,
  refreshReportDraft,
  returnReportDraft,
  saveReportDraft,
  submitReportDraft,
} from "./reportDraftApi";
import type {
  ReportDraft,
  ReportDraftInput,
  ReportMoney,
  ReportRowInput,
} from "./reportDraftApi";
import {
  copyInput,
  historyActionLabels,
  isValidMoney,
  moneyFields,
  moneyText,
  rebaseInput,
  reportAccess,
  reportError,
  reportReviewAccess,
  sameInput,
  sourceLabels,
  withdrawalStatusLabels,
} from "./reportDraftState";
import { RefundReportImport } from "./RefundReportImport";
import {
  ReportGuidanceDetails,
  ReportGuidanceSummary,
  ReportGuidanceTable,
} from "./ReportRowGuidance";
import {
  createReportDownloadGuard,
  prepareReportFinalExcel,
  reportDownloadAccess,
  reportDownloadContextKey,
  reportFinalExcelError,
  saveReportFinalExcel,
} from "./reportDownload";

const { Text } = Typography;

function Money({ data }: { data: ReportMoney }) {
  return (
    <Space direction="vertical" size={0}>
      <Text type={data.value === null ? "danger" : undefined}>
        {moneyText(data.value)}
      </Text>
      <Text type="secondary" style={{ fontSize: 11 }}>
        {sourceLabels[data.source] ?? data.source}
      </Text>
    </Space>
  );
}

type Operation = {
  action: "save" | "refresh" | "submit" | "approve" | "return";
  base: ReportDraft;
  edited: ReportDraftInput;
  note?: string;
};

export function RefundReportWidget({ id }: { id: string }) {
  const canEdit = useCanEdit("refund-orders");
  const role = useAppRole();
  const queryClient = useQueryClient();
  const queryKey = ["withdrawalReportDraft", id];
  const [report, setReport] = useState<ReportDraft | null>(null);
  const [input, setInput] = useState<ReportDraftInput | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("summary");
  const [operationError, setOperationError] = useState<string | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const downloadPending = useRef(false);
  const mounted = useRef(true);
  const downloadGuard = useRef(createReportDownloadGuard()).current;
  const [reviewDialog, setReviewDialog] = useState<"approve" | "return" | null>(
    null,
  );
  const [reviewText, setReviewText] = useState("");
  const dirtyRef = useRef(false);
  const query = useQuery({
    queryKey,
    queryFn: () => getReportDraft(id),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  useEffect(() => {
    if (query.data && !dirtyRef.current) {
      setReport(query.data);
      setInput(copyInput(query.data.input));
    }
  }, [query.data]);

  const dirty = !!report && !!input && !sameInput(report.input, input);
  downloadGuard.setContext(
    reportDownloadContextKey(report, query.data, dirty, query.isError, role),
  );
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      downloadGuard.invalidate();
    };
  }, [downloadGuard]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  function accept(next: ReportDraft, nextInput = copyInput(next.input)) {
    dirtyRef.current = !sameInput(next.input, nextInput);
    setReport(next);
    setInput(nextInput);
    queryClient.setQueryData(queryKey, next);
  }

  const mutation = useMutation({
    mutationFn: ({ action, base, edited, note }: Operation) => {
      const revision = {
        orderListId: id,
        expectedVersion: base.version,
        sourceHash: base.sourceHash,
      };
      if (action === "save")
        return saveReportDraft({ ...revision, input: edited });
      if (action === "refresh") return refreshReportDraft(revision);
      if (action === "approve")
        return approveReportDraft({ ...revision, reviewNote: note ?? "" });
      if (action === "return")
        return returnReportDraft({ ...revision, reason: note ?? "" });
      return submitReportDraft(revision);
    },
    onSuccess: (next, operation) => {
      setOperationError(null);
      if (operation.action === "refresh") {
        const rebased = rebaseInput(operation.base, operation.edited, next);
        accept(next, rebased.input);
        if (rebased.dropped)
          message.warning(
            `Эх шинэчлэгдлээ. Түгжигдсэн эсвэл хасагдсан талбарын ${rebased.dropped} өөрчлөлтийг шинэ эхээр сольсон.`,
          );
        else message.success("Эх шинэчлэгдлээ.");
        if (!sameInput(next.input, rebased.input))
          message.info(
            "Хадгалаагүй өөрчлөлтүүд үлдсэн. Нооргийг дахин хадгална уу.",
          );
      } else {
        accept(next);
        setReviewDialog(null);
        setReviewText("");
        message.success(
          operation.action === "save"
            ? "Ноорог хадгалагдлаа."
            : operation.action === "approve"
              ? "Тайлан батлагдлаа. Санхүүгийн болон шилжүүлгийн төлөв өөрчлөгдөөгүй."
              : operation.action === "return"
                ? "Тайлан засварт буцаагдлаа."
                : "Тайланг хяналтад илгээлээ.",
        );
      }
    },
    onError: (error) => {
      setOperationError(reportError(error));
      // Keep local edits; a read exposes a newer revision/source without replacing them.
      void query.refetch();
    },
  });

  function edit(next: ReportDraftInput) {
    if (!report) return;
    dirtyRef.current = !sameInput(report.input, next);
    setInput(next);
  }

  function editRow(
    key: keyof Omit<ReportRowInput, "requestId">,
    value: string,
  ) {
    if (!input || !selectedId) return;
    const exists = input.rows.some((row) => row.requestId === selectedId);
    const rows = exists
      ? input.rows
      : [...input.rows, { requestId: selectedId }];
    edit({
      ...input,
      rows: rows.map((row) =>
        row.requestId === selectedId
          ? { ...row, [key]: value === "" ? null : value }
          : row,
      ),
    });
  }

  if (!report || !input) {
    return (
      <div className="page-body">
        <Link to="/refund">← Буцаалтын багцууд</Link>
        {query.isError ? (
          <Alert
            style={{ marginTop: 20 }}
            type="error"
            showIcon
            message={reportError(query.error)}
            action={
              <Button onClick={() => query.refetch()}>Дахин оролдох</Button>
            }
          />
        ) : (
          <div style={{ padding: 60, textAlign: "center" }}>
            <Spin tip="Тайланг уншиж байна…" />
          </div>
        )}
      </div>
    );
  }

  const busy =
    mutation.isLoading || query.isFetching || importBusy || downloadLoading;
  const { remoteVersionChanged, sourceChanged, editable, canSubmit } =
    reportAccess(report, query.data, { canEdit, dirty, busy });
  const { canApprove, canReturn } = reportReviewAccess(report, query.data, {
    isAdmin: role === "ADMIN",
    dirty,
    busy,
  });
  const downloadAccess = reportDownloadAccess(report, query.data, {
    role,
    dirty,
    busy,
    queryError: query.isError,
  });
  const validation = [
    ...(!moment(input.asOfDate, "YYYY-MM-DD", true).isValid()
      ? ["Тайлангийн огноог зөв оруулна уу."]
      : []),
    ...input.rows.flatMap((row) =>
      moneyFields
        .filter(({ key }) => !isValidMoney(row[key]))
        .map(
          ({ label }) =>
            `${label}: сөрөг биш, бүхэл хэсэг 12 хүртэл оронтой, бутархай 2 хүртэл оронтой дүн оруулна уу.`,
        ),
    ),
  ];
  const selectedRow = report.rows.find((row) => row.requestId === selectedId);
  const guidanceStale =
    dirty || sourceChanged || remoteVersionChanged || query.isError;
  const guidanceByRequest = new Map(
    (report.rowGuidance ?? []).map((row) => [row.requestId, row.items]),
  );
  const affectedRequestCount = new Set(
    report.issues
      .filter((issue) => issue.requestId)
      .map((issue) => issue.requestId),
  ).size;
  const rowInput = input.rows.find((row) => row.requestId === selectedId);
  const errorCount = report.issues.filter(
    (issue) => issue.severity === "error",
  ).length;
  const lastSaved = [...report.history].sort((a, b) =>
    b.at.localeCompare(a.at),
  )[0];
  const totalLabel =
    report.version === 0 ? "Эхээс уншсан дүн" : "Хадгалсан хувилбарын дүн";
  const summaryLabels = [
    ...moneyFields.slice(0, 4),
    { key: "grossAmount" as const, label: "Шимтгэлийн өмнөх дүн" },
    moneyFields[4],
    { key: "transferAmount" as const, label: "Шилжүүлэх дүн" },
  ];
  const run = (action: Operation["action"], note?: string) =>
    mutation.mutate({ action, base: report, edited: copyInput(input), note });
  const openReview = (action: "approve" | "return") => {
    setOperationError(null);
    setReviewText("");
    setReviewDialog(action);
  };
  const downloadFinal = async () => {
    if (!downloadAccess.eligible || downloadPending.current) return;
    downloadPending.current = true;
    setDownloadLoading(true);
    setDownloadError(null);
    const token = downloadGuard.capture();
    const isCurrent = () => {
      if (downloadGuard.isCurrent(token)) return true;
      if (mounted.current)
        setDownloadError(
          "Тайлан эсвэл эх өгөгдөл өөрчлөгдсөн тул таталтыг зогсоолоо. Эхийг дахин шалгана уу.",
        );
      return false;
    };
    try {
      const response = await downloadReportFinalExcel({
        orderListId: report.orderListId,
        expectedVersion: report.version,
        sourceHash: report.sourceHash,
      });
      if (!isCurrent()) return;
      const file = await prepareReportFinalExcel(response, report.version);
      if (!isCurrent()) return;
      saveReportFinalExcel(file);
      message.success("Эцсийн Excel файлын таталт эхэллээ.");
    } catch (error) {
      if (mounted.current) {
        setDownloadError(reportFinalExcelError(error));
        void query.refetch();
      }
    } finally {
      downloadPending.current = false;
      if (mounted.current) setDownloadLoading(false);
    }
  };

  return (
    <div>
      <Prompt
        when={dirty}
        message="Хадгалаагүй өөрчлөлт байна. Энэ хуудаснаас гарах уу?"
      />
      <div className="page-header" style={{ flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3>Буцаалтын тайлан</h3>
          <Space wrap>
            <Tag
              color={
                report.state === "Approved"
                  ? "green"
                  : report.state === "InReview"
                    ? "blue"
                    : "default"
              }
            >
              {report.state === "Approved"
                ? "Тайлан батлагдсан"
                : report.state === "InReview"
                  ? "Хяналтад"
                  : "Ноорог"}
            </Tag>
            <Text type="secondary">
              {report.version === 0
                ? "Хадгалаагүй"
                : `Хувилбар ${report.version}`}{" "}
              · Багц:{" "}
              {withdrawalStatusLabels[report.batchStatus] ?? report.batchStatus}
            </Text>
            {dirty && <Tag color="orange">Хадгалаагүй өөрчлөлт</Tag>}
          </Space>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined />}
            disabled={busy}
            onClick={() => query.refetch()}
          >
            Эхийг шалгах
          </Button>
          {report.state === "Approved" && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={!downloadAccess.eligible}
              loading={downloadLoading}
              onClick={() => void downloadFinal()}
            >
              Эцсийн Excel татах
            </Button>
          )}
          {editable && (
            <Button
              icon={<SaveOutlined />}
              type="primary"
              disabled={
                busy ||
                sourceChanged ||
                remoteVersionChanged ||
                validation.length > 0 ||
                (!dirty && report.version > 0)
              }
              loading={
                mutation.isLoading && mutation.variables?.action === "save"
              }
              onClick={() => run("save")}
            >
              Ноорог хадгалах
            </Button>
          )}
          {editable && (
            <Popconfirm
              title="Энэ хувилбарыг хяналтад илгээх үү? Засах шаардлагатай бол ADMIN засварт буцаана."
              okText="Илгээх"
              cancelText="Болих"
              disabled={!canSubmit}
              onConfirm={() => {
                if (canSubmit) run("submit");
              }}
            >
              <Button
                icon={<SendOutlined />}
                disabled={!canSubmit}
                loading={
                  mutation.isLoading && mutation.variables?.action === "submit"
                }
              >
                Хяналтад илгээх
              </Button>
            </Popconfirm>
          )}
          {role === "ADMIN" && report.state === "InReview" && (
            <>
              <Button
                icon={<RollbackOutlined />}
                disabled={!canReturn}
                onClick={() => openReview("return")}
              >
                Засварт буцаах
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                disabled={!canApprove}
                onClick={() => openReview("approve")}
              >
                Тайлан батлах
              </Button>
            </>
          )}
          <Link to="/refund">
            <Button icon={<ArrowLeftOutlined />}>Буцах</Button>
          </Link>
        </Space>
      </div>
      <div className="page-body">
        <Space direction="vertical" style={{ width: "100%" }} size={16}>
          <Alert
            showIcon
            type="info"
            message="Буцаалтын тайлан · тооцоо ба эх баримтын хяналт"
            description="Excel импортлох, хяналтад илгээх, тайлан батлах, эцсийн файл татах нь санхүүгийн баталгаажуулалт эсвэл мөнгө шилжүүлсэн бүртгэл үүсгэхгүй. Эцсийн Excel-ийг батлагдсан хувилбараас татна."
          />
          {!canEdit && (
            <Alert
              showIcon
              type="info"
              message="Таны эрхээр тайланг уншиж, батлагдсан эцсийн Excel-ийг татаж болно."
            />
          )}
          {report.state === "InReview" && (
            <Alert
              showIcon
              type="success"
              message="Тайлан хяналтад илгээгдсэн. Хяналтын үед өгөгдлийг өөрчлөхгүй."
              description={
                role === "ADMIN"
                  ? "Бүрэн, зөрчилгүй, шинэ эхтэй бөгөөд шилжүүлэг хийж буй эсвэл дууссан багцын тайланг батална. Засах шаардлагатай бол шалтгаантайгаар засварт буцаана."
                  : "ADMIN тайланг хянаж батлах эсвэл засварт буцаана. Энэ нь санхүүгийн баталгаажуулалт, шилжүүлгийн төлөвийг өөрчлөхгүй."
              }
            />
          )}
          {report.state === "Approved" && (
            <Alert
              showIcon
              type="success"
              message="Тайлан батлагдсан · Энэ хувилбар өөрчлөгдөхгүй"
              description={
                <Space direction="vertical" size={4}>
                  <span>
                    Зөвхөн тайлангийн хяналт батлагдсан. Багцын санхүүгийн болон
                    шилжүүлгийн төлөв өөрчлөгдөөгүй.
                  </span>
                  <span>
                    Эцсийн файл нь батлагдсан дүн болон хянасан Excel-ийн 8
                    хуудсыг агуулна. Хадгалсан томьёоны үр дүнг тогтмол утга
                    болгон ашиглана; санхүүгийн тооцоог шинээр хийхгүй.
                  </span>
                  {!downloadAccess.eligible && !downloadLoading && (
                    <Text type="warning">{downloadAccess.blockedReason}</Text>
                  )}
                  {report.approval && (
                    <>
                      <span>
                        Баталсан:{" "}
                        {moment(report.approval.at).format(
                          "YYYY-MM-DD HH:mm:ss",
                        )}{" "}
                        · Хянасан хувилбар: {report.approval.fromVersion}
                      </span>
                      <span>Хяналтын тэмдэглэл: {report.approval.note}</span>
                    </>
                  )}
                </Space>
              }
            />
          )}
          {operationError && (
            <Alert type="error" showIcon message={operationError} />
          )}
          {downloadError && (
            <Alert
              type="error"
              showIcon
              message={downloadError}
              description="Дахин татах нь зөвхөн файл үүсгэх ажиллагааг давтана. Тайлангийн баталгаа болон санхүүгийн төлөв өөрчлөгдөхгүй."
            />
          )}
          {query.isError && (
            <Alert
              type="warning"
              showIcon
              message="Эхийг дахин шалгаж чадсангүй. Хамгийн сүүлд уншсан тайланг харуулж байна."
            />
          )}
          {remoteVersionChanged && (
            <Alert
              type="warning"
              showIcon
              message="Тайлангийн шинэ хувилбар өөр газраас хадгалагдсан."
              description="Таны бичсэн өөрчлөлтийг хэвээр үлдээлээ. Шинэ хувилбарыг уншихын өмнө шаардлагатай утгуудаа нягтална уу."
              action={
                <Popconfirm
                  title="Хадгалаагүй өөрчлөлтийг орхиод шинэ хувилбарыг унших уу?"
                  okText="Шинэ хувилбар унших"
                  cancelText="Болих"
                  onConfirm={() => query.data && accept(query.data)}
                >
                  <Button disabled={busy}>Шинэ хувилбар унших</Button>
                </Popconfirm>
              }
            />
          )}
          {sourceChanged && (
            <Alert
              type="warning"
              showIcon
              message="Хадгалсан эхээс хойш өгөгдөл өөрчлөгдсөн."
              description={
                editable
                  ? "Эхийг шинэчилсний дараа дүн болон банкны мэдээллээ дахин хянана уу. Хадгалаагүй засваруудаас шинэ эхээр түгжигдээгүй талбарыг үлдээх бөгөөд тэдгээрийг дахин хадгалах шаардлагатай."
                  : "Энэ тайлан хадгалсан эхээ харуулж байна. Хяналтад байгаа эсвэл батлагдсан хувилбарын өгөгдөл автоматаар шинэчлэгдэхгүй."
              }
              action={
                editable && (
                  <Button
                    disabled={busy || remoteVersionChanged}
                    onClick={() => run("refresh")}
                  >
                    Эхийг шинэчлэх
                  </Button>
                )
              }
            />
          )}
          {dirty && (
            <Alert
              type="warning"
              showIcon
              message="Өөрчлөлтөө хадгалсны дараа хяналтад илгээнэ."
              description="Нийт дүн, мөрийн тооцоо болон шалгалтын үр дүн хамгийн сүүлд уншсан / хадгалсан хувилбарынх. Өөрчлөлт хадгалагдсаны дараа шинэчлэгдэнэ."
              action={
                <Popconfirm
                  title="Хадгалаагүй өөрчлөлтийг буцаах уу?"
                  okText="Буцаах"
                  cancelText="Болих"
                  onConfirm={() => {
                    dirtyRef.current = false;
                    setInput(copyInput(report.input));
                  }}
                >
                  <Button disabled={busy}>Өөрчлөлт буцаах</Button>
                </Popconfirm>
              }
            />
          )}
          {validation.length > 0 && (
            <Alert
              type="error"
              showIcon
              message={[...new Set(validation)].join(" ")}
            />
          )}
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "summary",
                label: "Хураангуй",
                children: (
                  <Space
                    direction="vertical"
                    size={16}
                    style={{ width: "100%" }}
                  >
                    <Card size="small" title="Тайлангийн мэдээлэл">
                      <Form layout="vertical">
                        <Row gutter={24}>
                          <Col xs={24} md={8}>
                            <Form.Item
                              label={
                                <label htmlFor="report-date">
                                  Тайлангийн огноо
                                </label>
                              }
                              extra="Огноо өөрчлөх нь баталгаажсан дүнг дахин тооцохгүй."
                            >
                              <Input
                                id="report-date"
                                type="date"
                                value={input.asOfDate}
                                disabled={
                                  !editable || busy || remoteVersionChanged
                                }
                                onChange={(event) =>
                                  edit({
                                    ...input,
                                    asOfDate: event.target.value,
                                  })
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={16}>
                            <Form.Item
                              label={
                                <label htmlFor="report-note">
                                  Тайлангийн тайлбар
                                </label>
                              }
                            >
                              <Input.TextArea
                                id="report-note"
                                rows={2}
                                value={input.note ?? ""}
                                disabled={
                                  !editable || busy || remoteVersionChanged
                                }
                                onChange={(event) =>
                                  edit({
                                    ...input,
                                    note: event.target.value || null,
                                  })
                                }
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form>
                      <Descriptions
                        size="small"
                        column={{ xs: 1, sm: 2, md: 3 }}
                      >
                        <Descriptions.Item label="Багц">
                          {report.orderListId}
                        </Descriptions.Item>
                        <Descriptions.Item label="Хүсэлт">
                          {report.rows.length}
                        </Descriptions.Item>
                        <Descriptions.Item label="Харилцагч">
                          {new Set(report.rows.map((row) => row.userId)).size}
                        </Descriptions.Item>
                      </Descriptions>
                      <Text type="secondary">
                        Багцын бүх хүсэлтийг хамруулна. Дутуу өгөгдөлтэй хүсэлт
                        хасагдахгүй.
                      </Text>
                    </Card>
                    <Card size="small" title={totalLabel}>
                      <p>
                        <Text>
                          Шилжүүлэх дүн = хөрөнгийн цэвэр дүн + бэлэн мөнгө −
                          банкны шимтгэл.
                        </Text>
                      </p>
                      <Row gutter={[16, 16]}>
                        {summaryLabels.map(({ key, label }) => (
                          <Col xs={24} sm={12} lg={8} key={key}>
                            <Text type="secondary">{label}</Text>
                            <div
                              style={{
                                fontSize: 20,
                                fontVariantNumeric: "tabular-nums",
                              }}
                            >
                              <Text
                                strong
                                type={
                                  report.summary[key] === null
                                    ? "danger"
                                    : undefined
                                }
                              >
                                {moneyText(report.summary[key])}
                              </Text>
                            </div>
                          </Col>
                        ))}
                      </Row>
                      <div style={{ marginTop: 16 }}>
                        <Text type="secondary">
                          {report.state === "Approved"
                            ? "Батлагдсан тайлангийн дүн. Энэ тайлан нь мөнгө шилжүүлсэн баримтыг орлохгүй."
                            : "Дутуу утгыг 0 гэж тооцохгүй. Хамаарахгүй хөрөнгийн 0 дүнг эх нь тодорхойлно. Эцсийн санхүүгийн дүн биш."}
                        </Text>
                      </div>
                    </Card>
                    <Card
                      size="small"
                      title={`Батлахад саадтай ${errorCount} шалгалт`}
                      extra={
                        <Button onClick={() => setActiveTab("guidance")}>
                          Зөрүү ба хийх ажил ({affectedRequestCount})
                        </Button>
                      }
                    >
                      <p>
                        <Text type="secondary">
                          {affectedRequestCount} хүсэлтэд шалгах зүйл байна. Нэг
                          зөрүү нийт болон шилжүүлэх дүнгийн хэд хэдэн шалгалтад
                          давхар орж болно. Шалтгаан, засах ажил, бүрдүүлэх
                          баримтыг «Зөрүү ба хийх ажил»-аас харна уу.
                        </Text>
                      </p>
                      {report.issues.length ? (
                        <List
                          dataSource={report.issues}
                          renderItem={(issue, index) => (
                            <List.Item
                              key={`${issue.code}-${issue.requestId}-${index}`}
                              actions={
                                issue.requestId
                                  ? [
                                      <Button
                                        type="link"
                                        key="open"
                                        onClick={() =>
                                          setSelectedId(issue.requestId!)
                                        }
                                      >
                                        Шалтгаан, хийх ажил
                                      </Button>,
                                    ]
                                  : []
                              }
                            >
                              <Space>
                                <Tag
                                  color={
                                    issue.severity === "error"
                                      ? "red"
                                      : "orange"
                                  }
                                >
                                  {issue.severity === "error"
                                    ? "Засах"
                                    : "Анхаарах"}
                                </Tag>
                                <span>
                                  {issue.message}
                                  {issue.requestId && (
                                    <Text type="secondary">
                                      {" "}
                                      ·{" "}
                                      {report.rows.find(
                                        (row) =>
                                          row.requestId === issue.requestId,
                                      )?.displayName ?? issue.requestId}
                                    </Text>
                                  )}
                                </span>
                              </Space>
                            </List.Item>
                          )}
                        />
                      ) : (
                        <Text>
                          Хадгалсан өгөгдлийн шалгалтаар зөрүү илрээгүй.
                        </Text>
                      )}
                      {editable && !canSubmit && (
                        <div style={{ marginTop: 12 }}>
                          <Text type="secondary">
                            Хяналтад илгээхэд ноорог хадгалагдсан, өгөгдөл
                            бүрэн, эх болон хувилбар шинэ байх шаардлагатай.
                          </Text>
                        </div>
                      )}
                    </Card>
                  </Space>
                ),
              },
              {
                key: "guidance",
                label: "Зөрүү ба хийх ажил",
                children: (
                  <Space
                    direction="vertical"
                    size={16}
                    style={{ width: "100%" }}
                  >
                    <ReportGuidanceTable
                      report={report}
                      stale={guidanceStale}
                      onOpen={setSelectedId}
                    />
                    {!!report.batchGuidance?.length && (
                      <Card size="small" title="Багц / Excel-ийн нийт шалгалт">
                        <ReportGuidanceDetails
                          items={report.batchGuidance}
                          stale={guidanceStale}
                        />
                      </Card>
                    )}
                  </Space>
                ),
              },
              {
                key: "import",
                label: "Excel тулгалт",
                children: (
                  <RefundReportImport
                    report={report}
                    latest={query.data}
                    dirty={dirty}
                    busy={busy}
                    canEdit={canEdit}
                    guidanceStale={guidanceStale}
                    onOpenRow={setSelectedId}
                    onApplied={(next) => {
                      setOperationError(null);
                      accept(next);
                    }}
                    onBusyChange={setImportBusy}
                    onRefresh={() => {
                      void query.refetch();
                    }}
                  />
                ),
              },
              {
                key: "rows",
                label: `Мөрүүд (${report.rows.length})`,
                children: (
                  <Table
                    size="small"
                    bordered
                    rowKey="requestId"
                    dataSource={report.rows}
                    scroll={{ x: 1880 }}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                    columns={[
                      {
                        title: "Харилцагч / хүсэлт",
                        key: "user",
                        width: 230,
                        fixed: "left",
                        render: (_, row) => (
                          <Space direction="vertical" size={0}>
                            <Button
                              type="link"
                              style={{ padding: 0, height: "auto" }}
                              onClick={() => setSelectedId(row.requestId)}
                            >
                              {row.displayName || "Нэргүй харилцагч"}
                            </Button>
                            <Text type="secondary">{row.email}</Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {withdrawalStatusLabels[row.requestStatus] ??
                                row.requestStatus}{" "}
                              · Х:{row.stockCount} Б:{row.bondCount} ҮХ:
                              {row.estateCount}
                            </Text>
                          </Space>
                        ),
                      },
                      ...moneyFields.map(({ key, label }) => ({
                        title: label,
                        key,
                        width: 170,
                        render: (
                          _: unknown,
                          row: ReportDraft["rows"][number],
                        ) => <Money data={row[key]} />,
                      })),
                      {
                        title: "Шилжүүлэх дүн",
                        key: "transfer",
                        width: 165,
                        render: (_, row) => moneyText(row.transferAmount),
                      },
                      {
                        title: "Зөрүүний шалтгаан / хийх ажил",
                        key: "guidance",
                        width: 330,
                        render: (_, row) => (
                          <ReportGuidanceSummary
                            items={guidanceByRequest.get(row.requestId) ?? []}
                            stale={guidanceStale}
                            onOpen={() => setSelectedId(row.requestId)}
                          />
                        ),
                      },
                      {
                        title: "Банк / данс",
                        key: "bank",
                        width: 200,
                        render: (_, row) => (
                          <Space direction="vertical" size={0}>
                            <Text type={row.bankName ? undefined : "danger"}>
                              {row.bankName || "Банк дутуу"}
                            </Text>
                            <Text type={row.accountNo ? undefined : "danger"}>
                              {row.accountNo || "Данс дутуу"}
                            </Text>
                          </Space>
                        ),
                      },
                      {
                        title: "",
                        key: "edit",
                        width: 160,
                        fixed: "right",
                        render: (_, row) => (
                          <Button onClick={() => setSelectedId(row.requestId)}>
                            {editable ? "Өгөгдөл нөхөх" : "Дэлгэрэнгүй"}
                          </Button>
                        ),
                      },
                    ]}
                  />
                ),
              },
              {
                key: "sources",
                label: "Эх өгөгдөл",
                children: (
                  <Card size="small" title="Өгөгдлийн эх ба түгжээ">
                    <List
                      dataSource={[
                        [
                          "Баталгаажсан дүн",
                          "Санхүүгийн баталгаажуулалтаас уншсан дүн. Тайлангаас өөрчлөхгүй.",
                        ],
                        [
                          "Хүсэлтэд хадгалсан эх",
                          "Хүсэлтийн бэлэн мөнгөний хадгалсан дүн. Огноо солиход өөрчлөгдөхгүй.",
                        ],
                        [
                          "Бүртгэлтэй банк",
                          "Эхэд байгаа банк, дансны мэдээллийг түгжинэ. Дутуу талбарыг л нөхнө.",
                        ],
                        [
                          "Гараар нөхсөн · Нооргийн дүн",
                          "Баталгаажуулаагүй хөрөнгийн цэвэр дүн, дутуу бэлэн мөнгө, банк эсвэл шимтгэлийг эхийн тайлбартай оруулна. Энэ нь санхүүгийн баталгаажуулалт биш.",
                        ],
                        [
                          "Excel-ээс нөхсөн · Тайлангийн дүн",
                          "Хуучин Excel-ийн Нийт хуудаснаас дутуу талбарыг нөхсөн. Эх файл, тулгалт болон зөрчлийг Excel тулгалт хэсэгт хадгална. Энэ нь санхүүгийн баталгаажуулалт биш.",
                        ],
                        [
                          "Эх дутуу",
                          "Хоосон утга. 0 бол зөвхөн илэрхий оруулсан тоо; хоосон талбарыг 0 болгохгүй.",
                        ],
                        [
                          "Хамаарахгүй",
                          "Тухайн хүсэлтэд хамаарахгүй хөрөнгийн дүнг эх тодорхойлсон.",
                        ],
                      ]}
                      renderItem={([title, description]) => (
                        <List.Item>
                          <List.Item.Meta
                            title={title}
                            description={description}
                          />
                        </List.Item>
                      )}
                    />
                    <Descriptions
                      column={1}
                      size="small"
                      style={{ marginTop: 16 }}
                    >
                      <Descriptions.Item label="Эхийн төлөв">
                        {sourceChanged
                          ? "Хадгалснаас хойш өөрчлөгдсөн"
                          : "Хамгийн сүүлд шалгасан эхтэй тохирч байна"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Хадгалсан хувилбар">
                        {report.version || "Хараахан хадгалаагүй"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Сүүлд хадгалсан">
                        {lastSaved
                          ? moment(lastSaved.at).format("YYYY-MM-DD HH:mm:ss")
                          : "Хараахан хадгалаагүй"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Эхийг шалгасан">
                        {moment(query.dataUpdatedAt).format(
                          "YYYY-MM-DD HH:mm:ss",
                        )}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                ),
              },
              {
                key: "history",
                label: "Түүх",
                children: report.history.length ? (
                  <Table
                    size="small"
                    rowKey={(row) => `${row.version}-${row.action}-${row.at}`}
                    dataSource={report.history}
                    pagination={false}
                    columns={[
                      { title: "Хувилбар", dataIndex: "version" },
                      {
                        title: "Үйлдэл",
                        dataIndex: "action",
                        render: (value: string) =>
                          historyActionLabels[value] ?? value,
                      },
                      {
                        title: "Огноо",
                        dataIndex: "at",
                        render: (value: string) =>
                          moment(value).isValid()
                            ? moment(value).format("YYYY-MM-DD HH:mm:ss")
                            : value,
                      },
                      { title: "Ажилтан", dataIndex: "actorId" },
                      { title: "Тайлбар", dataIndex: "message" },
                    ]}
                  />
                ) : (
                  <Empty description="Хадгалсан түүх хараахан алга." />
                ),
              },
            ]}
          />
        </Space>
      </div>
      <Modal
        title={reviewDialog === "approve" ? "Тайлан батлах" : "Засварт буцаах"}
        open={reviewDialog !== null}
        okText={reviewDialog === "approve" ? "Тайлан батлах" : "Засварт буцаах"}
        cancelText="Болих"
        confirmLoading={mutation.isLoading}
        closable={!mutation.isLoading}
        maskClosable={!mutation.isLoading}
        cancelButtonProps={{ disabled: mutation.isLoading }}
        okButtonProps={{
          disabled:
            !reviewText.trim() ||
            (reviewDialog === "approve" ? !canApprove : !canReturn),
        }}
        onCancel={() => {
          if (!mutation.isLoading) setReviewDialog(null);
        }}
        onOk={() => {
          if (
            !reviewDialog ||
            !reviewText.trim() ||
            (reviewDialog === "approve" ? !canApprove : !canReturn)
          )
            return;
          run(reviewDialog, reviewText.trim());
        }}
      >
        <Alert
          showIcon
          type="info"
          style={{ marginBottom: 16 }}
          message={
            reviewDialog === "approve"
              ? "Зөвхөн тайлангийн хяналтыг батална."
              : "Тайланг засах боломжтой ноорог төлөвт буцаана."
          }
          description="Санхүүгийн баталгаажуулалт болон шилжүүлгийн бүртгэлийг өөрчлөхгүй."
        />
        {operationError && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={operationError}
          />
        )}
        <Form layout="vertical">
          <Form.Item
            required
            label={
              <label htmlFor="report-review-note">
                {reviewDialog === "approve"
                  ? "Хяналтын тэмдэглэл"
                  : "Буцаах шалтгаан"}
              </label>
            }
          >
            <Input.TextArea
              id="report-review-note"
              autoFocus
              rows={4}
              maxLength={2000}
              showCount
              value={reviewText}
              disabled={mutation.isLoading}
              onChange={(event) => setReviewText(event.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Drawer
        title={selectedRow?.displayName || "Хүсэлтийн өгөгдөл"}
        width={Math.min(760, window.innerWidth)}
        open={!!selectedRow}
        onClose={() => setSelectedId(null)}
        footer={
          <Space>
            <Button onClick={() => setSelectedId(null)}>Хаах</Button>
            {editable && (
              <Text type="secondary">
                Өөрчлөлтөө тайлангийн «Ноорог хадгалах» товчоор хадгална.
              </Text>
            )}
          </Space>
        }
      >
        {selectedRow && (
          <Form layout="vertical">
            <p>
              <Text type="secondary">
                {selectedRow.email} ·{" "}
                {withdrawalStatusLabels[selectedRow.requestStatus] ??
                  selectedRow.requestStatus}
              </Text>
            </p>
            <Card
              size="small"
              title="Зөрүүний шалтгаан, хийх ажил, баримт"
              style={{ marginBottom: 20 }}
            >
              <ReportGuidanceDetails
                items={guidanceByRequest.get(selectedRow.requestId) ?? []}
                stale={guidanceStale}
              />
            </Card>
            <Alert
              showIcon
              type="info"
              style={{ marginBottom: 20 }}
              message="Гараар нөхсөн дүн бол нооргийн дүн."
              description="Эх дутуу талбарыг л нөхнө. 0 дүнг илэрхий оруулна; хоосон үлдээвэл дутуу гэж үзнэ. Мөнгөний дүн бүр төгрөгөөр."
            />
            {moneyFields.map(({ key, label }) => {
              const field = selectedRow[key];
              const value = field.locked
                ? field.value
                : (rowInput?.[key] ?? null);
              return (
                <Form.Item
                  key={key}
                  label={<label htmlFor={`report-${key}`}>{label}</label>}
                  validateStatus={!isValidMoney(value) ? "error" : undefined}
                  help={
                    !isValidMoney(value)
                      ? "Сөрөг биш тоо; бүхэл 12, бутархай 2 хүртэл орон."
                      : undefined
                  }
                  extra={
                    field.locked
                      ? `${sourceLabels[field.source]} · Түгжээтэй`
                      : field.source === "ExcelImport" && value === field.value
                        ? sourceLabels.ExcelImport
                        : value !== null
                          ? "Гараар нөхсөн · Нооргийн дүн"
                          : "Эх дутуу · 0-ээс ялгаатай"
                  }
                >
                  <Input
                    id={`report-${key}`}
                    inputMode="decimal"
                    value={value ?? ""}
                    disabled={
                      field.locked || !editable || busy || remoteVersionChanged
                    }
                    placeholder="Дутуу"
                    suffix="₮"
                    onChange={(event) => editRow(key, event.target.value)}
                  />
                </Form.Item>
              );
            })}
            <Form.Item
              label={<label htmlFor="report-bank">Банк</label>}
              extra={
                selectedRow.bankNameLocked
                  ? "Бүртгэлтэй банк · Түгжээтэй"
                  : "Дутуу банкны нэрийг нөхөх"
              }
            >
              <Input
                id="report-bank"
                value={
                  selectedRow.bankNameLocked
                    ? (selectedRow.bankName ?? "")
                    : (rowInput?.bankName ?? "")
                }
                disabled={
                  selectedRow.bankNameLocked ||
                  !editable ||
                  busy ||
                  remoteVersionChanged
                }
                onChange={(event) => editRow("bankName", event.target.value)}
              />
            </Form.Item>
            <Form.Item
              label={<label htmlFor="report-account">Дансны дугаар</label>}
              extra={
                selectedRow.accountNoLocked
                  ? "Бүртгэлтэй данс · Түгжээтэй"
                  : "Эхэд тулгуурлан дансны дугаарыг нөхөх"
              }
            >
              <Input
                id="report-account"
                value={
                  selectedRow.accountNoLocked
                    ? (selectedRow.accountNo ?? "")
                    : (rowInput?.accountNo ?? "")
                }
                disabled={
                  selectedRow.accountNoLocked ||
                  !editable ||
                  busy ||
                  remoteVersionChanged
                }
                onChange={(event) => editRow("accountNo", event.target.value)}
              />
            </Form.Item>
            <Form.Item
              label={
                <label htmlFor="report-source-note">
                  Нөхсөн утгын эх / тайлбар
                </label>
              }
              extra="Баримтын нэр, огноо, дугаар эсвэл холбоос, хянасан тайлбараа бичнэ. Тайлбар хадгалах нь дүнгийн зөрчлийг өөрөө арилгахгүй."
            >
              <Input.TextArea
                id="report-source-note"
                rows={3}
                value={rowInput?.sourceNote ?? ""}
                disabled={!editable || busy || remoteVersionChanged}
                onChange={(event) => editRow("sourceNote", event.target.value)}
              />
            </Form.Item>
            <Descriptions column={1} size="small" title={totalLabel}>
              <Descriptions.Item label="Шимтгэлийн өмнөх дүн">
                {moneyText(selectedRow.grossAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="Шилжүүлэх дүн">
                {moneyText(selectedRow.transferAmount)}
              </Descriptions.Item>
              <Descriptions.Item label="Хүсэлтийн ID">
                {selectedRow.requestId}
              </Descriptions.Item>
            </Descriptions>
            {report.issues
              .filter((issue) => issue.requestId === selectedId)
              .map((issue, index) => (
                <Alert
                  key={`${issue.code}-${index}`}
                  style={{ marginTop: 12 }}
                  type={issue.severity === "error" ? "error" : "warning"}
                  message={issue.message}
                />
              ))}
          </Form>
        )}
      </Drawer>
    </div>
  );
}
